/**
 * TELEFON NORMALIZASYONU — F-PPL-06
 *
 * Sartname: "Telefon E.164 normalize edilir; GECERSIZ NUMARA KAYDEDILMEZ."
 *
 * Neden onemli: numara serbest metin olarak saklanirsa ayni kisi
 * `0532 111 22 33`, `+90 532 111 22 33` ve `532-111-2233` olarak uc kez
 * kaydedilebilir; SMS gonderimi bunlarin bir kismina ulasmaz ve
 * "mukerrer kayit" temizligi imkansiz hale gelir.
 *
 * Bu dosya SAF. Kutuphane kullanilmiyor: yalnizca Turkiye numaralarini
 * destekliyoruz ve kural kucuk. Yurt disi numarasi gerektiginde
 * `libphonenumber` eklenir — o zaman bu dosya onun sarmalayicisi olur.
 *
 * ================================================================
 * NEDEN `kisiler` MODULUNDEN BURAYA TASINDI (05.09.2026)
 * ================================================================
 * Kural artik IKI is tarafindan kullaniliyor: kayit (F-PPL-06) ve
 * GIRIS (G-AUT-00 — telefonla giris numarayi E.164'e cevirip ariyor).
 * `auth` modulunun `kisiler` modulunden import etmesi kurali iki eve
 * tasimazdi ama KATMAN SINIRINI bulanikstirirdi; bir sonraki kisi
 * "madem oradan aliyoruz" diyip ikinci bir kopya acardi.
 *
 * Burada olmasinin ikinci sebebi: panel de ayni kurali gorebilmeli.
 *
 * DIKKAT — `packages/contracts` SIFIR BAGIMLILIKLI kalmali: buraya
 * Prisma, NestJS ya da `@koclukpanel/db` tipi girmez. Girseydi panelin
 * derlemesine sunucu agaci dahil olurdu.
 */

/** Turkiye ulke kodu. Tek firma, tek merkez — varsayilan bu. */
export const ULKE_KODU = "90";

export type TelefonHatasi =
  "BOS" | "GECERSIZ_KARAKTER" | "UZUNLUK" | "GECERSIZ_OPERATOR" | "DESTEKLENMEYEN_ULKE";

export const TELEFON_MESAJI: Record<TelefonHatasi, string> = {
  BOS: "Telefon numarası boş olamaz.",
  GECERSIZ_KARAKTER: "Telefon numarası yalnızca rakam içerebilir.",
  UZUNLUK: "Telefon numarası 10 haneli olmalı (örnek: 532 111 22 33).",
  GECERSIZ_OPERATOR: "Cep telefonu numarası 5 ile başlamalı.",
  DESTEKLENMEYEN_ULKE: "Şimdilik yalnızca Türkiye numaraları kaydedilebiliyor.",
};

export type TelefonSonuc =
  { gecerli: true; e164: string } | { gecerli: false; neden: TelefonHatasi };

/**
 * Girdiyi E.164'e cevirir: `+905321112233`
 *
 * Kabul edilen bicimler:
 *   0532 111 22 33 · 532-111-2233 · +90 532 111 22 33 · 90 532 111 22 33
 *   (0532) 111 22 33
 */
export function telefonNormalize(ham: string): TelefonSonuc {
  const temiz = ham.trim();
  if (temiz.length === 0) return { gecerli: false, neden: "BOS" };

  // Bosluk, tire, parantez ve nokta AYIRICIDIR — atilir. Bunlarin disinda
  // bir sey kalirsa (harf gibi) numara bozuktur; sessizce silmiyoruz.
  const ayiricisiz = temiz.replace(/[\s\-().]/g, "");
  const artiVarMi = ayiricisiz.startsWith("+");
  const rakamlar = artiVarMi ? ayiricisiz.slice(1) : ayiricisiz;

  if (!/^\d+$/.test(rakamlar)) return { gecerli: false, neden: "GECERSIZ_KARAKTER" };

  const yerel = ulkeKodunuAyikla(rakamlar, artiVarMi);
  if (yerel === null) return { gecerli: false, neden: "DESTEKLENMEYEN_ULKE" };

  if (yerel.length !== 10) return { gecerli: false, neden: "UZUNLUK" };
  // Cep numaralari 5 ile baslar. Sabit hat kabul etmiyoruz: bildirimler
  // SMS ve WhatsApp uzerinden gidiyor, sabit hatta ulasmaz.
  if (!yerel.startsWith("5")) return { gecerli: false, neden: "GECERSIZ_OPERATOR" };

  return { gecerli: true, e164: `+${ULKE_KODU}${yerel}` };
}

/**
 * Ulke kodunu ayirip 10 haneli yerel numarayi doner.
 * `null` = Turkiye disi bir ulke kodu.
 */
function ulkeKodunuAyikla(rakamlar: string, artiVarMi: boolean): string | null {
  // +90 ... ya da 90 ... ile baslayan 12 haneli
  if (rakamlar.startsWith(ULKE_KODU) && rakamlar.length === 12) {
    return rakamlar.slice(2);
  }
  // Basinda + varsa ve 90 ile baslamiyorsa baska ulke demektir.
  // + YOKSA bu dala girmiyoruz: `0532...` da `5` ile baslamayan bir
  // rakam dizisi ve ulke kodu sanilmamali.
  if (artiVarMi && !rakamlar.startsWith(ULKE_KODU)) return null;

  // 0 ile baslayan yerel bicim: 0532 111 22 33
  if (rakamlar.startsWith("0") && rakamlar.length === 11) {
    return rakamlar.slice(1);
  }
  return rakamlar;
}

/** Ekranda gosterim: +90 532 111 22 33 */
export function telefonBicimle(e164: string): string {
  const m = /^\+90(\d{3})(\d{3})(\d{2})(\d{2})$/.exec(e164);
  if (!m) return e164; // taninmayan bicimi bozmadan geri ver
  return `+90 ${m[1]} ${m[2]} ${m[3]} ${m[4]}`;
}
